import React, { useMemo } from 'react';
import { sluggify } from 'utils/localization/utils';
import isNil from 'lodash/isNil';
import formatMoney from 'utils/formatters/Money';

import {
  checkedFormatter,
  orderIDRenderer,
  titleizeHumanizeSlug,
} from '../formatters';
import Badge from 'components/v2/Badge';
import BillingStatusBadge from 'components/v2/BillingStatusBadge';
import ShipmentStatusBadge from 'components/v2/ShipmentStatusBadge';
import {
  booleanDefaultFilterParams,
  currencyFormatter,
  documentsFilterParams,
  dateFormatter,
  dateServerSideFilterParams,
  getNumberFilter,
  percentFormatter,
  textDefaultFilterParams,
  missingReferenceNumbersFilterParams,
  missingAccessorialsFilterParams,
  pendingAccessorialsFilterParams,
} from '../columns';
import TextFilterStartsWith from '../customFilters/TextFilterStartsWith';
import { orderStatuses } from './orderStatuses';
import {
  billingStatusList,
  billableEventList,
  invoiceGroupingFilterList,
} from 'constants/billing-status';
import { orderTypes } from 'config/order-types';
import { HoverBadges } from '../customCellRenderers/HoverBadges';
import CargomaticTooltip from 'components/v2/Tooltip';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import { NO_DATA } from 'utils/constants';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@cargomatic/ui';

const standardRefNumberValueGetter = (params, field) => {
  if (Array.isArray(params?.data?.reference_numbers)) {
    const ref = params?.data?.reference_numbers;
    const data = ref.find((r) => r.name === field);
    return data?.value ?? null;
  }
  return null;
};

export const orderColumns = [
  {
    headerName: 'Order ID',
    field: 'order_id',
    width: 180,
    cellRenderer: orderIDRenderer,
    pinned: 'left',
    checkboxSelection: true,
    headerCheckboxSelection: true,
    filter: TextFilterStartsWith,
  },
  {
    headerName: 'Origin',
    field: 'origin',
    width: 150,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Destination',
    field: 'destination',
    width: 150,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Billing Status',
    groupId: 'billingStatus',
    children: [
      {
        headerName: 'Billable Event',
        field: 'billing_status_billable_event',
        width: 150,
        filter: 'agSetColumnFilter',
        cellRenderer: ({ value }) => {
          return value ? (
            <Badge color="green">{titleizeHumanizeSlug(value)}</Badge>
          ) : null;
        },
        filterParams: {
          buttons: ['reset', 'apply'],
          valueFormatter: ({ value }) => titleizeHumanizeSlug(value),
          values: billableEventList,
        },
      },
      {
        headerName: 'Billing Status',
        field: 'order_billing_status',
        width: 150,
        filter: 'agSetColumnFilter',
        cellRenderer: ({ value }) => {
          return value ? <BillingStatusBadge billingStatus={value} /> : null;
        },
        filterParams: {
          buttons: ['reset', 'apply'],
          valueFormatter: ({ value }) =>
            value ? titleizeHumanizeSlug(value) : '(Blank)',
          values: ['', ...billingStatusList],
        },
      },
      {
        headerName: 'Invoice Grouping',
        field: 'billing_status_invoice_grouping',
        width: 150,
        cellRenderer: ({ value }) =>
          value ? (
            <Badge color="green">{titleizeHumanizeSlug(value)}</Badge>
          ) : null,
        filter: 'agSetColumnFilter',
        filterParams: {
          buttons: ['reset', 'apply'],
          valueFormatter: ({ value }) => {
            return titleizeHumanizeSlug(value);
          },
          values: invoiceGroupingFilterList,
        },
      },
      // {
      //   headerName: 'Override Billing Status Note',
      //   field: 'order_pause_automated_billing_evaluation_reason_code',
      //   width: 200,
      //   filter: 'textFilterWithExclude',
      //   wrapHeaderText: true,
      //   cellClass: '!whitespace-normal',
      // },
      {
        headerName: 'Pending Accessorial Evaluation',
        field: 'billing_status_pending_accessorial_evaluation',
        width: 200,
        cellDataType: 'boolean',
        filter: 'agSetColumnFilter',
        filterParams: booleanDefaultFilterParams,
        wrapHeaderText: true,
      },
      {
        headerName: 'State/Missing',
        groupId: 'billingStatusStateMissing',
        children: [
          {
            headerName: 'Unbilled Charges',
            field: 'order_pending_unbilled_total',
            width: 150,
            cellDataType: 'number',
            cellRenderer: ({
              data: { order_pending_unbilled_total, shipment_currency } = {},
            }) => {
              if (isNil(order_pending_unbilled_total)) {
                return '—';
              }
              return formatMoney(
                order_pending_unbilled_total,
                shipment_currency,
              );
            },
            ...getNumberFilter(),
          },
          {
            headerName: 'Required Docs',
            field: 'order_has_required_docs',
            cellDataType: 'boolean',
            width: 130,
            cellRenderer: checkedFormatter,
            filter: 'agSetColumnFilter',
            filterParams: booleanDefaultFilterParams,
          },
          {
            headerName: 'Missing Docs',
            field: 'order_missing_docs',
            cellRenderer: HoverBadges,
            width: 130,
            filter: 'agSetColumnFilter',
            filterParams: documentsFilterParams,
          },
          {
            headerName: 'Required Reference Numbers',
            field: 'order_has_required_reference_numbers',
            cellRenderer: checkedFormatter,
            width: 200,
            cellDataType: 'boolean',
            filter: 'agSetColumnFilter',
            filterParams: booleanDefaultFilterParams,
          },
          {
            headerName: 'Missing Reference Numbers',
            field: 'order_missing_reference_numbers',
            width: 200,
            cellRenderer: HoverBadges,
            filter: 'agSetColumnFilter',
            filterParams: missingReferenceNumbersFilterParams,
          },
          {
            headerName: 'Missing Accessorials',
            field: 'order_missing_required_accessorial_types',
            cellRenderer: HoverBadges,
            width: 160,
            filter: 'agSetColumnFilter',
            filterParams: missingAccessorialsFilterParams,
          },
          {
            headerName: 'Pending Accessorials',
            field: 'order_pending_required_accessorial_types',
            cellRenderer: HoverBadges,
            width: 160,
            filter: 'agSetColumnFilter',
            filterParams: pendingAccessorialsFilterParams,
          },
        ],
      },
    ],
  },
  {
    headerName: 'Accounting Summary',
    groupId: 'accountingSummary',
    children: [
      {
        headerName: 'Total Revenue',
        field: 'total_invoiced',
        width: 140,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: currencyFormatter,
        wrapHeaderText: true,
      },
      {
        headerName: 'Unactioned Pending Revenue',
        field: 'unactioned_pending_revenue',
        width: 210,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        cellRenderer: ({ data }) => {
          const val = data?.unactioned_pending_revenue;
          if (isNil(val)) return NO_DATA;
          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="underline decoration-dotted cursor-default"
                  >
                    {formatMoney(val)}
                  </button>
                </TooltipTrigger>
                <TooltipContent className="w-auto p-3" align="start">
                  <div className="flex flex-col gap-2">
                    <div className="text-sm whitespace-nowrap">
                      <span className="font-medium">
                        Unbilled Approved Revenue:
                      </span>{' '}
                      <span className="font-bold">
                        {formatMoney(data?.unbilled_approved)}
                      </span>
                    </div>
                    <div className="text-sm whitespace-nowrap">
                      <span className="font-medium">
                        Pending Accessorial Revenue:
                      </span>{' '}
                      <span className="font-bold">
                        {formatMoney(data?.pending_accessorial_revenue)}
                      </span>
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        },
        wrapHeaderText: true,
      },
      {
        headerName: 'Unbilled Approved Revenue',
        field: 'unbilled_approved',
        width: 200,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: currencyFormatter,
        wrapHeaderText: true,
      },
      {
        headerName: 'Pending Accessorial Revenue',
        field: 'pending_accessorial_revenue',
        width: 210,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: currencyFormatter,
        wrapHeaderText: true,
      },
      {
        headerName: 'Carrier Cost',
        field: 'total_carrier_payout',
        width: 140,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: currencyFormatter,
        wrapHeaderText: true,
      },
      {
        headerName: 'Vendor Cost',
        field: 'total_vendor_payout',
        width: 140,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: currencyFormatter,
        wrapHeaderText: true,
      },
      {
        headerName: 'Unactioned Pending COGS',
        field: 'unactioned_pending_cogs',
        width: 200,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: currencyFormatter,
        wrapHeaderText: true,
      },
      {
        headerName: 'Gross Profit',
        field: 'gross_profit',
        width: 140,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueGetter: ({ data }) => {
          const totalInvoiced = data?.total_invoiced;
          const unactionedPendingRevenue = data?.unactioned_pending_revenue;
          const totalCarrierPayout = data?.total_carrier_payout;
          const totalVendorPayout = data?.total_vendor_payout;
          const unactionedPendingCogs = data?.unactioned_pending_cogs;
          if (
            isNil(totalInvoiced) &&
            isNil(unactionedPendingRevenue) &&
            isNil(totalCarrierPayout) &&
            isNil(totalVendorPayout) &&
            isNil(unactionedPendingCogs)
          )
            return null;
          return (
            (totalInvoiced ?? 0) +
            (unactionedPendingRevenue ?? 0) -
            ((totalCarrierPayout ?? 0) +
              (totalVendorPayout ?? 0) +
              (unactionedPendingCogs ?? 0))
          );
        },
        cellRenderer: ({ data, value }) => {
          if (isNil(value)) return NO_DATA;
          const bookedGrossProfit =
            (data?.total_invoiced ?? 0) -
            ((data?.total_carrier_payout ?? 0) +
              (data?.total_vendor_payout ?? 0));
          const pendingGrossProfit =
            (data?.unactioned_pending_revenue ?? 0) -
            (data?.unactioned_pending_cogs ?? 0);
          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className={`underline decoration-dotted cursor-default ${
                      value > 0 ? 'text-green-600' : ''
                    }`}
                  >
                    {formatMoney(value)}
                  </button>
                </TooltipTrigger>
                <TooltipContent className="w-auto p-3" align="start">
                  <div className="flex flex-col gap-2">
                    <div className="text-sm whitespace-nowrap">
                      <span className="font-medium">Booked Gross Profit:</span>{' '}
                      <span className="font-bold">
                        {formatMoney(bookedGrossProfit)}
                      </span>
                    </div>
                    <div className="text-sm whitespace-nowrap">
                      <span className="font-medium">Pending Gross Profit:</span>{' '}
                      <span className="font-bold">
                        {formatMoney(pendingGrossProfit)}
                      </span>
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        },
        wrapHeaderText: true,
      },
      {
        headerName: 'Booked Gross Profit',
        field: 'booked_gross_profit',
        width: 170,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueGetter: ({ data }) => {
          const totalInvoiced = data?.total_invoiced;
          const totalCarrierPayout = data?.total_carrier_payout;
          const totalVendorPayout = data?.total_vendor_payout;
          if (
            isNil(totalInvoiced) &&
            isNil(totalCarrierPayout) &&
            isNil(totalVendorPayout)
          )
            return null;
          return (
            (totalInvoiced ?? 0) -
            ((totalCarrierPayout ?? 0) + (totalVendorPayout ?? 0))
          );
        },
        valueFormatter: currencyFormatter,
        wrapHeaderText: true,
      },
      {
        headerName: 'Pending Gross Profit',
        field: 'pending_gross_profit',
        width: 170,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueGetter: ({ data }) => {
          const unactionedPendingRevenue = data?.unactioned_pending_revenue;
          const unactionedPendingCogs = data?.unactioned_pending_cogs;
          if (isNil(unactionedPendingRevenue) && isNil(unactionedPendingCogs))
            return null;
          return (unactionedPendingRevenue ?? 0) - (unactionedPendingCogs ?? 0);
        },
        valueFormatter: currencyFormatter,
        wrapHeaderText: true,
      },
      {
        headerName: 'Gross Margin',
        field: 'margin_pct',
        width: 140,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: percentFormatter,
        filterValueGetter: (params) => {
          const val = params.data?.margin_pct;
          if (isNil(val)) return null;
          const num = Number(val);
          return Number.isFinite(num) ? num * 100 : null;
        },
        wrapHeaderText: true,
      },
    ],
  },
  {
    headerName: 'Vessel',
    field: 'vessel_or_rail',
    width: 160,
    filter: 'textFilterWithExclude',
    cellClass: '!whitespace-normal',
    autoHeight: true,
  },
  {
    headerName: 'Accounting Review Date',
    field: 'accounting_review_date',
    width: 180,
    filter: 'agDateColumnFilter',
    filterParams: dateServerSideFilterParams,
    valueFormatter: dateFormatter,
    wrapHeaderText: true,
  },
  {
    headerName: 'Automated Billing Evaluated Paused Timestamp',
    field: 'order_pause_automated_billing_evaluation_timestamp',
    width: 280,
    filter: 'agDateColumnFilter',
    filterParams: dateServerSideFilterParams,
    valueFormatter: dateFormatter,
    wrapHeaderText: true,
  },
  {
    headerName: 'Automated Billing Evaluation Paused',
    field: 'order_pause_automated_billing_evaluation',
    valueGetter: ({ data }) =>
      data?.order_pause_automated_billing_evaluation ?? false,
    width: 250,
    cellDataType: 'boolean',
    filter: 'agSetColumnFilter',
    filterParams: booleanDefaultFilterParams,
    wrapHeaderText: true,
  },
  {
    headerName: 'Automated Billing Evaluation Paused Reason',
    field: 'order_pause_automated_billing_evaluation_reason_code',
    width: 280,
    filter: 'textFilterWithExclude',
    wrapHeaderText: true,
  },
  {
    headerName: 'Base Rate',
    field: 'base_rate',
    width: 120,
    filter: 'agNumberColumnFilter',
    type: 'rightAligned',
    valueFormatter: currencyFormatter,
  },
  {
    headerName: 'BCO',
    field: 'bco',
    width: 120,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Cargo Description',
    field: 'cargo_description',
    width: 180,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Carrier Accessorial Total',
    field: 'carrier_accessorial_total',
    width: 180,
    filter: 'agNumberColumnFilter',
    type: 'rightAligned',
    valueFormatter: currencyFormatter,
    wrapHeaderText: true,
  },
  {
    headerName: 'Carrier Cost Total',
    field: 'carrier_cost_total',
    width: 160,
    filter: 'agNumberColumnFilter',
    type: 'rightAligned',
    valueFormatter: currencyFormatter,
  },
  {
    headerName: 'Carrier Fsc Total',
    field: 'carrier_fsc_total',
    width: 150,
    filter: 'agNumberColumnFilter',
    type: 'rightAligned',
    valueFormatter: currencyFormatter,
  },
  {
    headerName: 'Carrier Linehaul',
    field: 'carrier_linehaul',
    width: 150,
    filter: 'agNumberColumnFilter',
    type: 'rightAligned',
    valueFormatter: currencyFormatter,
  },
  {
    headerName: 'Carrier Other Accessorial Total',
    field: 'carrier_other_accessorial_total',
    width: 220,
    filter: 'agNumberColumnFilter',
    type: 'rightAligned',
    valueFormatter: currencyFormatter,
  },
  {
    headerName: 'Carrier Total',
    field: 'carrier_total',
    width: 130,
    filter: 'agNumberColumnFilter',
    type: 'rightAligned',
    valueFormatter: currencyFormatter,
  },
  {
    headerName: 'Container Number',
    field: 'container_numbers',
    width: 160,
    filter: 'textFilterWithExclude',
    valueFormatter: ({ value }) =>
      Array.isArray(value) ? value.join('\n') : value,
    cellClass: 'whitespace-pre-wrap leading-relaxed',
    autoHeight: true,
  },
  {
    headerName: 'Date Created',
    field: 'created_at',
    width: 160,
    filter: 'agDateColumnFilter',
    filterParams: dateServerSideFilterParams,
    valueFormatter: dateFormatter,
  },
  {
    headerName: 'Created By',
    field: 'created_by',
    width: 150,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Destination City',
    field: 'destination_city',
    width: 150,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Destination Name',
    field: 'destination_name',
    width: 180,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Destination State',
    field: 'destination_state',
    width: 150,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Hazmat Class',
    field: 'hazmat_class',
    width: 130,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Hazmat Description',
    field: 'hazmat_description',
    width: 180,
    filter: 'textFilterWithExclude',
    cellClass: '!whitespace-normal',
  },
  {
    headerName: 'Hazmat Packaging',
    field: 'hazmat_packaging_group',
    width: 160,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Invoice Cliff Date',
    field: 'invoice_cliff_date',
    width: 160,
    filter: 'agDateColumnFilter',
    filterParams: dateServerSideFilterParams,
    valueFormatter: dateFormatter,
  },
  {
    headerName: 'Market',
    field: 'market_code',
    width: 120,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Mode',
    field: 'order_mode',
    width: 100,
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'],
      valueFormatter: ({ value }) => titleizeHumanizeSlug(value),
      values: ['drayage', 'intermodal'],
    },
  },
  {
    headerName: 'Move Type',
    field: 'move_type',
    width: 120,
    filterParams: {
      ...textDefaultFilterParams,
    },
  },
  {
    headerName: 'Order Carrier',
    field: 'carrier_name',
    width: 150,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Order Received Date',
    field: 'order_received_date',
    width: 180,
    filter: 'agDateColumnFilter',
    filterParams: dateServerSideFilterParams,
    valueFormatter: dateFormatter,
  },
  {
    headerName: 'Order Status',
    field: 'orderStatus',
    width: 130,
    filter: 'agSetColumnFilter',
    cellRenderer: ({ value }) => {
      return value ? <ShipmentStatusBadge status={value} /> : null;
    },
    filterParams: {
      buttons: ['reset', 'apply'],
      valueFormatter: ({ value }) => titleizeHumanizeSlug(value),
      values: orderStatuses,
    },
  },
  {
    headerName: 'Order Timezone',
    field: 'order_timezone',
    width: 150,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Order Type',
    field: 'order_type',
    width: 120,
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'],
      valueFormatter: ({ value }) => titleizeHumanizeSlug(value),
      values: orderTypes,
    },
  },
  {
    headerName: 'Order Updated',
    field: 'order_updated',
    width: 160,
    filter: 'agDateColumnFilter',
    filterParams: dateServerSideFilterParams,
    valueFormatter: dateFormatter,
  },
  {
    headerName: 'Origin City',
    field: 'origin_city',
    width: 130,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Origin Name',
    field: 'origin_name',
    width: 160,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Origin State',
    field: 'origin_state',
    width: 130,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Pending Carrier Accessorials',
    field: 'pending_carrier_accessorials',
    width: 200,
    filter: 'agNumberColumnFilter',
    type: 'rightAligned',
  },
  {
    headerName: 'Pending Shipper Accessorials',
    field: 'pending_shipper_accessorials',
    width: 200,
    filter: 'agNumberColumnFilter',
    type: 'rightAligned',
  },
  {
    headerName: 'Railroads',
    field: 'railroads',
    width: 150,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Reference Numbers',
    field: 'reference_numbers',
    width: 180,
    filter: 'textFilterWithExclude',
    filterValueGetter: (params) => {
      const refs = params.data?.reference_numbers;
      if (!refs || !Array.isArray(refs)) return '';
      return refs.map((ref) => `${ref.name} ${ref.value}`).join(' ');
    },
    autoHeight: true,
    cellRenderer: ({ value }) => {
      if (!value || !Array.isArray(value) || value.length === 0) {
        return null;
      }
      const items = value.map((r, index) => {
        const label = titleizeHumanizeSlug(sluggify(r.name));
        return (
          <DescriptionListItem
            key={index}
            dataTestId={`DescriptionListItem-uv-orders-ref-numbers-${index}`}
            className="mb-2"
            label={label}
          >
            {r.value}
          </DescriptionListItem>
        );
      });
      return (
        <CargomaticTooltip
          dataTestId="CargomaticTooltip-b9ff2365"
          body={() => items.slice(0, 2)}
          position="top"
        >
          <div data-testid="uv-orders-ref-numbers">{items}</div>
        </CargomaticTooltip>
      );
    },
  },
  {
    headerName: 'Service Type',
    field: 'service_type',
    width: 130,
    filter: 'agSetColumnFilter',
  },
  {
    headerName: 'Shipper',
    field: 'shipper_name',
    width: 180,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Shipper Accessorial Total',
    field: 'shipper_accessorial_total',
    width: 180,
    filter: 'agNumberColumnFilter',
    type: 'rightAligned',
    valueFormatter: currencyFormatter,
  },
  {
    headerName: 'Shipper Account Support Agent',
    field: 'shipper_account_support_agent',
    width: 220,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Shipper Account Support Agent Tier2',
    field: 'shipper_account_support_agent_tier2',
    width: 250,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Shipper Billing Rep',
    field: 'shipper_billing_rep',
    width: 170,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Shipper Chassis Total',
    field: 'shipper_chassis_total',
    width: 170,
    filter: 'agNumberColumnFilter',
    type: 'rightAligned',
    valueFormatter: currencyFormatter,
  },
  {
    headerName: 'Shipper Cost Total',
    field: 'shipper_cost_total',
    width: 160,
    filter: 'agNumberColumnFilter',
    type: 'rightAligned',
    valueFormatter: currencyFormatter,
  },
  {
    headerName: 'Shipper Customer Success Manager',
    field: 'shipper_customer_success_manager',
    width: 240,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Shipper Data Entry Agent',
    field: 'shipper_data_entry_agent',
    width: 190,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Shipper Demurrage Total',
    field: 'shipper_demurrage_total',
    width: 190,
    filter: 'agNumberColumnFilter',
    type: 'rightAligned',
    valueFormatter: currencyFormatter,
  },
  {
    headerName: 'Shipper FSC Total',
    field: 'shipper_fsc_total',
    width: 150,
    filter: 'agNumberColumnFilter',
    type: 'rightAligned',
    valueFormatter: currencyFormatter,
  },
  {
    headerName: 'Shipper Linehaul',
    field: 'shipper_linehaul',
    width: 150,
    filter: 'agNumberColumnFilter',
    type: 'rightAligned',
  },
  {
    headerName: 'Shipper Ops Group',
    field: 'shipper_operations_group',
    width: 160,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Shipper Ops Manager',
    field: 'shipper_operations_manager',
    width: 170,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Shipper Other Accessorial Total',
    field: 'shipper_other_accessorial_total',
    width: 220,
    filter: 'agNumberColumnFilter',
    type: 'rightAligned',
    valueFormatter: currencyFormatter,
  },
  {
    headerName: 'Shipper Sales Rep',
    field: 'shipper_sales_rep',
    width: 160,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Shipper Storage Total',
    field: 'shipper_storage_total',
    width: 170,
    filter: 'agNumberColumnFilter',
    type: 'rightAligned',
    valueFormatter: currencyFormatter,
  },
  {
    headerName: 'Shipper Total',
    field: 'shipper_total',
    width: 130,
    filter: 'agNumberColumnFilter',
    type: 'rightAligned',
    valueFormatter: currencyFormatter,
  },
  {
    headerName: 'Standard Reference Numbers',
    groupId: 'standardRefNumbers',
    children: [
      {
        headerName: 'Booking Number',
        field: 'booking_number',
        width: 150,
        filter: 'textFilterWithExclude',
        valueGetter: (params) =>
          standardRefNumberValueGetter(params, 'booking_number'),
      },
      {
        headerName: 'Category',
        field: 'category',
        width: 120,
        filter: 'textFilterWithExclude',
        valueGetter: (params) =>
          standardRefNumberValueGetter(params, 'category'),
      },
      {
        headerName: 'Delivery Order',
        field: 'delivery_order',
        width: 150,
        filter: 'textFilterWithExclude',
        valueGetter: (params) =>
          standardRefNumberValueGetter(params, 'delivery_order'),
      },
      {
        headerName: 'Final Destination',
        field: 'final_destination',
        width: 150,
        filter: 'textFilterWithExclude',
        valueGetter: (params) =>
          standardRefNumberValueGetter(params, 'final_destination'),
      },
      {
        headerName: 'Master BOL',
        field: 'master_bol',
        width: 130,
        filter: 'textFilterWithExclude',
        valueGetter: (params) =>
          standardRefNumberValueGetter(params, 'master_bol'),
      },
      {
        headerName: 'MO Number',
        field: 'mo_number',
        width: 130,
        filter: 'textFilterWithExclude',
        valueGetter: (params) =>
          standardRefNumberValueGetter(params, 'mo_number'),
      },
      {
        headerName: 'Mode',
        field: 'Mode',
        width: 100,
        filter: 'textFilterWithExclude',
        valueGetter: (params) => standardRefNumberValueGetter(params, 'Mode'),
      },
      {
        headerName: 'PO Number',
        field: 'po_number',
        width: 130,
        filter: 'textFilterWithExclude',
        valueGetter: (params) =>
          standardRefNumberValueGetter(params, 'po_number'),
      },
      {
        headerName: 'Work Order Number',
        field: 'work_order_number',
        width: 160,
        filter: 'textFilterWithExclude',
        valueGetter: (params) =>
          standardRefNumberValueGetter(params, 'work_order_number'),
      },
    ],
  },
  {
    headerName: 'Steamship',
    field: 'steamship_or_railroad',
    width: 150,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Team Commercial Account Manager',
    field: 'commercial_account_manager',
    width: 240,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Team Commercial Sales Rep',
    field: 'commercial_sales_rep',
    width: 210,
    filter: 'textFilterWithExclude',
  },
  // next two under common header?
  {
    headerName: 'Team Drayage Ops Customer Success',
    field: 'drayage_ops_customer_success',
    width: 260,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Team Drayage Ops Data Entry',
    field: 'drayage_ops_data_entry',
    width: 220,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Total',
    field: 'total',
    width: 120,
    filter: 'agNumberColumnFilter',
    type: 'rightAligned',
    valueFormatter: currencyFormatter,
  },
  {
    headerName: 'Total Invoiced',
    field: 'total_invoiced',
    width: 140,
    filter: 'agNumberColumnFilter',
    type: 'rightAligned',
    valueFormatter: currencyFormatter,
  },
  {
    headerName: 'Train',
    field: 'train',
    width: 120,
    filter: 'textFilterWithExclude',
  },
  {
    headerName: 'Unbilled Approved',
    field: 'unbilled_approved',
    width: 160,
    filter: 'agNumberColumnFilter',
    type: 'rightAligned',
  },
  {
    headerName: 'Unbilled Charges',
    field: 'unbilled_charges',
    width: 150,
    filter: 'agNumberColumnFilter',
    type: 'rightAligned',
  },
  {
    headerName: 'Unbilled Pending',
    field: 'unbilled_pending',
    width: 150,
    filter: 'agNumberColumnFilter',
    type: 'rightAligned',
  },
  {
    headerName: 'Vendor Total',
    field: 'vendor_total',
    width: 130,
    filter: 'agNumberColumnFilter',
    type: 'rightAligned',
    valueFormatter: currencyFormatter,
  },
  {
    headerName: 'Weight',
    field: 'total_weight',
    width: 120,
    filter: 'agNumberColumnFilter',
    type: 'rightAligned',
  },
];

export const useOrderColumns = ({ onOrderClick } = {}) => {
  return useMemo(() => {
    const cols = orderColumns.map((col) => {
      if (col.field === 'order_id') {
        return {
          ...col,
          cellRendererParams: { onOrderClick },
        };
      }
      return col;
    });
    return cols;
  }, [onOrderClick]);
};
